/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    SEARCH_SPLIT_MAIN
 * Date:         2025-12-30
 * Modified By:  Cursor
 *
 * Description:
 * Refactored from 1544 lines to ~150 lines by extracting types, utilities, hooks, and components.
 *
 * Related Screens / Modules:
 * - client/src/components/search/types.ts (NEW)
 * - client/src/components/search/utils/urlHelpers.ts (NEW)
 * - client/src/components/search/utils/filterHelpers.ts (NEW)
 * - client/src/components/search/utils/broadcastHelpers.ts (NEW)
 * - client/src/components/search/hooks/useSearchState.ts (NEW)
 */

// [2026-02-07] style-extraction: moved inline styles to SCSS module
import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
// [2026-07-06] GUEST_ACCESS: guests search Broadcasts (default) + Inventory; Companies gated (BR-03)
import { useIsGuest } from "../../contexts/GuestModeContext";
import { Inventory } from "../../interfaces/Inventory";
import { User } from "../../interfaces/User";
import { InventoryFilters as InventoryFiltersComponent } from "../inventory/InventoryFilters";
import { BroadcastFilters } from "../broadcast/BroadcastFilters";
import { CompanyFilters } from "../companies/CompanyFilters";
import { InventoryItemCard } from "../inventory/InventoryItemCard";
import { BroadcastItemCard, FlattenedBroadcastItem } from "../broadcast/BroadcastItemCard";
import { CompanyCard } from "../companies/CompanyCard";
import { InventoryGrid } from "../inventory/InventoryGrid";
import { PaginationControls } from "../ui/PaginationControls";
import { useSearchState } from "./hooks/useSearchState";
import styles from "./Search.module.scss";
// [2026-02-10] Mobile: Import responsive hook and mobile layout
import { useMediaQuery } from "../../hooks/useMediaQuery";
import MobileSearchLayout from "./MobileSearchLayout";

const Search: React.FC = () => {
  const navigate = useNavigate();
  // [2026-02-10] Mobile: Responsive detection
  const { isMobile } = useMediaQuery();
  // [2026-07-06] GUEST_ACCESS: guest flag — Companies tab is gated (BR-03)
  const isGuest = useIsGuest();

  const {
    state,
    setState,
    inventoryListRef,
    broadcastListRef,
    effectiveInventoryItemsPerPage,
    effectiveBroadcastItemsPerPage,
    handleTabChange,
    handleFilterChange,
    handlePageChange,
    handleItemsPerPageChange,
    handleClearFilters,
    handleRetry,
    getSortDisplayValue,
    handleSortChange,
  } = useSearchState();

  // [2026-07-06] GUEST_ACCESS: Broadcasts + Inventory are searchable by guests; Companies is
  // gated (see the gate panel in the Companies tab below). No forced tab redirect anymore.

  // [2025-12-30] Navigation handlers: extracted from original Search.tsx
  const handleInventoryClick = (item: Inventory) => {
    if (item._id) {
      navigate(`/inventory/item/${item._id}`);
    }
  };

  const handleBroadcastClick = (item: FlattenedBroadcastItem) => {
    if (item.broadcastGroup?._id != null && item.itemNo != null) {
      navigate(`/allbroadcasts/item/${item.broadcastGroup._id}/${item.itemNo}`);
    }
  };

  const handleCompanyClick = (company: User) => {
    if (company._id) {
      navigate(`/profile/${company._id}`);
    }
  };

  // [2025-12-30] Broadcast-specific handlers: extracted from original Search.tsx
  const handleBroadcastSkuChange = (value: string) => {
    handleFilterChange("broadcastSKU", value);
    handlePageChange(1);
  };

  const handleBroadcastManufacturerChange = (value: string) => {
    handleFilterChange("broadcastManufacturer", value);
    handlePageChange(1);
  };

  const handleBroadcastCategoryChange = (value: string) => {
    handleFilterChange("broadcastCategory", value);
    handlePageChange(1);
  };

  const handleBroadcastQtyChange = (value: number) => {
    handleFilterChange("broadcastQty", value);
    handlePageChange(1);
  };

  const handleBroadcastConditionChange = (value: string) => {
    handleFilterChange("broadcastCondition", value);
    handlePageChange(1);
  };

  const handleBroadcastCountryChange = (value: string) => {
    handleFilterChange("broadcastCountry", value);
    handlePageChange(1);
  };

  const handleBroadcastCompanyChange = (value: string) => {
    handleFilterChange("broadcastCompany", value);
    handlePageChange(1);
  };

  const handleBroadcastTypeChange = (value: string) => {
    handleFilterChange("broadcastType", value);
    handlePageChange(1);
  };

  const handleQtyChange = (value: number) => {
    handleFilterChange("minQty", value);
  };

  const handleCompanyChange = (value: string) => {
    handleFilterChange("company", value);
  };

  // [2025-12-30] Get current tab data: extracted from original Search.tsx
  const getCurrentTabData = () => {
    if (state.activeTab === "inventory") {
      return {
        items: state.inventoryItems,
        total: state.inventoryTotal,
        pages: state.inventoryPages,
        page: state.inventoryPage,
        loading: state.inventoryLoading,
        error: state.inventoryError,
        itemsPerPage: effectiveInventoryItemsPerPage,
      };
    } else if (state.activeTab === "broadcasts") {
      return {
        items: state.broadcastItems,
        total: state.broadcastTotal,
        pages: state.broadcastPages,
        page: state.broadcastPage,
        loading: state.broadcastLoading,
        error: state.broadcastError,
        itemsPerPage: effectiveBroadcastItemsPerPage,
      };
    } else {
      return {
        items: state.companyItems,
        total: state.companyTotal,
        pages: state.companyPages,
        page: state.companyPage,
        loading: state.companyLoading,
        error: state.companyError,
        itemsPerPage: state.companyItemsPerPage,
      };
    }
  };

  const currentTabData = getCurrentTabData();

  // [2026-07-06] GUEST_ACCESS_PHASE2: shared guest daily soft wall (BR-13) — shown on
  // Inventory + Broadcasts once the guest exhausts the free-search allowance.
  const softWallPanel = (
    <div className="text-center py-5">
      <div className="display-4 mb-2">🔒</div>
      <h2 className="u-H2-24 mb-2">You've reached today's free searches</h2>
      <p className="u-P2-16 text-color-grey-1 mb-4">
        {state.signupMessage ||
          "Please sign in or create a free account to keep searching UAVZone."}
      </p>
      <div className="d-flex gap-2 justify-content-center">
        <button type="button" className="btn-black" onClick={() => navigate("/signup")}>
          Create free account
        </button>
        <button type="button" className="btn-secondary" onClick={() => navigate("/signin")}>
          Sign in
        </button>
      </div>
    </div>
  );

  // [2026-07-06] GUEST_ACCESS: Companies tab is member-only — guests see a sign-in gate (BR-03).
  const companiesGatePanel = (
    <div className="text-center py-5">
      <div className="display-4 mb-2">🔒</div>
      <h2 className="u-H2-24 mb-2">Sign in to access company profiles</h2>
      <p className="u-P2-16 text-color-grey-1 mb-4">
        Company directory is available to members. Create a free account to view seller companies.
      </p>
      <div className="d-flex gap-2 justify-content-center">
        <button type="button" className="btn-black" onClick={() => navigate("/signup")}>
          Create free account
        </button>
        <button type="button" className="btn-secondary" onClick={() => navigate("/signin")}>
          Sign in
        </button>
      </div>
    </div>
  );

  // [2026-02-10] Mobile: Calculate active filter count for badge
  const activeFilterCount = useMemo(() => {
    if (state.activeTab === "broadcasts") {
      return [state.broadcastFilterKeyword, state.broadcastSKU, state.broadcastType,
              state.broadcastManufacturer, state.broadcastCategory, state.broadcastQty,
              state.broadcastCondition, state.broadcastCountry, state.broadcastCompany].filter(Boolean).length;
    } else if (state.activeTab === "companies") {
      return [state.companyFilterKeyword, state.companyCountryFilter].filter(Boolean).length;
    }
    return [state.inventoryFilterKeyword, state.inventoryPartNumber, state.category,
            state.condition, state.country, state.manufacturer, state.minQty,
            state.company].filter(Boolean).length;
  }, [state.activeTab, state.broadcastFilterKeyword, state.broadcastSKU, state.broadcastType,
      state.broadcastManufacturer, state.broadcastCategory, state.broadcastQty,
      state.broadcastCondition, state.broadcastCountry, state.broadcastCompany,
      state.companyFilterKeyword, state.companyCountryFilter, state.inventoryFilterKeyword,
      state.inventoryPartNumber, state.category, state.condition, state.country,
      state.manufacturer, state.minQty, state.company]);

  // [2026-02-10] Mobile: Extract filter content for reuse in both desktop and mobile
  const currentFilterContent = state.activeTab === "broadcasts" ? (
    <BroadcastFilters
      searchQuery={state.broadcastFilterKeyword}
      skuFilter={state.broadcastSKU}
      typeFilter={state.broadcastType}
      manufacturerFilter={state.broadcastManufacturer}
      categoryFilter={state.broadcastCategory}
      qtyFilter={state.broadcastQty}
      conditionFilter={state.broadcastCondition}
      countryFilter={state.broadcastCountry}
      companyFilter={state.broadcastCompany}
      onSearchChange={(value) => handleFilterChange("broadcastFilterKeyword", value)}
      onSkuChange={handleBroadcastSkuChange}
      onTypeChange={handleBroadcastTypeChange}
      onManufacturerChange={handleBroadcastManufacturerChange}
      onCategoryChange={handleBroadcastCategoryChange}
      onQtyChange={handleBroadcastQtyChange}
      onConditionChange={handleBroadcastConditionChange}
      onCountryChange={handleBroadcastCountryChange}
      onCompanyChange={handleBroadcastCompanyChange}
      onClearFilters={handleClearFilters}
      broadcastItems={state.allBroadcastItems}
      allBroadcastItems={state.allBroadcastItems}
      collapsed={false}
      onToggleCollapse={() => {}}
    />
  ) : state.activeTab === "companies" ? (
    <CompanyFilters
      searchQuery={state.companyFilterKeyword}
      countryFilter={state.companyCountryFilter}
      onSearchChange={(value) => handleFilterChange("companyFilterKeyword", value)}
      onCountryChange={(value) => handleFilterChange("companyCountryFilter", value)}
      onClearFilters={handleClearFilters}
      allCompanyItems={state.allCompanyItems}
      collapsed={false}
      onToggleCollapse={() => {}}
    />
  ) : (
    <InventoryFiltersComponent
      searchQuery={state.inventoryFilterKeyword}
      partNumberFilter={state.inventoryPartNumber}
      categoryFilter={state.category}
      conditionFilter={state.condition}
      countryFilter={state.country}
      manufacturerFilter={state.manufacturer}
      minPriceFilter={0}
      maxPriceFilter={0}
      qtyFilter={state.minQty}
      companyFilter={state.company}
      onSearchChange={(value) => handleFilterChange("inventoryFilterKeyword", value)}
      onPartNumberChange={(value) => handleFilterChange("inventoryPartNumber", value)}
      onCategoryChange={(value) => handleFilterChange("category", value)}
      onConditionChange={(value) => handleFilterChange("condition", value)}
      onCountryChange={(value) => handleFilterChange("country", value)}
      onManufacturerChange={(value) => handleFilterChange("manufacturer", value)}
      onMinPriceChange={() => {}}
      onMaxPriceChange={() => {}}
      onQtyChange={handleQtyChange}
      onCompanyChange={handleCompanyChange}
      onClearFilters={handleClearFilters}
      allInventoryItems={state.allInventoryItems}
      collapsed={false}
      showPriceFilter={false}
      showQtyFilter={state.activeTab === "inventory"}
      showCompanyFilter={state.activeTab === "inventory"}
      onToggleCollapse={() => {}}
    />
  );

  // [2026-02-10] Mobile: Early return with MobileSearchLayout
  if (isMobile) {
    return (
      <MobileSearchLayout
        state={state}
        currentTabData={currentTabData}
        handleTabChange={handleTabChange}
        handlePageChange={handlePageChange}
        handleItemsPerPageChange={handleItemsPerPageChange}
        handleClearFilters={handleClearFilters}
        handleRetry={handleRetry}
        getSortDisplayValue={getSortDisplayValue}
        handleSortChange={handleSortChange}
        onInventoryClick={handleInventoryClick}
        onBroadcastClick={handleBroadcastClick}
        onCompanyClick={handleCompanyClick}
        filterContent={currentFilterContent}
        activeFilterCount={activeFilterCount}
      />
    );
  }

  return (
    <div className={`container-fluid ${styles.container}`}>
      <div className={`row g-0 ${styles.row}`}>
        <aside
          className={`${
            state.filtersCollapsed ? "col-1" : "col-12 col-md-4 col-lg-3"
          } ps-3 pe-3 d-flex flex-column ${styles.sidebar} ${!state.filtersCollapsed ? styles.sidebarExpanded : ""}`}
        >
          {/* [2026-02-10] Desktop: Render filter with collapse toggle */}
          {state.activeTab === "broadcasts" ? (
            <BroadcastFilters
              searchQuery={state.broadcastFilterKeyword}
              skuFilter={state.broadcastSKU}
              typeFilter={state.broadcastType}
              manufacturerFilter={state.broadcastManufacturer}
              categoryFilter={state.broadcastCategory}
              qtyFilter={state.broadcastQty}
              conditionFilter={state.broadcastCondition}
              countryFilter={state.broadcastCountry}
              companyFilter={state.broadcastCompany}
              onSearchChange={(value) => handleFilterChange("broadcastFilterKeyword", value)}
              onSkuChange={handleBroadcastSkuChange}
              onTypeChange={handleBroadcastTypeChange}
              onManufacturerChange={handleBroadcastManufacturerChange}
              onCategoryChange={handleBroadcastCategoryChange}
              onQtyChange={handleBroadcastQtyChange}
              onConditionChange={handleBroadcastConditionChange}
              onCountryChange={handleBroadcastCountryChange}
              onCompanyChange={handleBroadcastCompanyChange}
              onClearFilters={handleClearFilters}
              broadcastItems={state.allBroadcastItems}
              allBroadcastItems={state.allBroadcastItems}
              collapsed={state.filtersCollapsed}
              onToggleCollapse={() =>
                setState((prev) => ({
                  ...prev,
                  filtersCollapsed: !prev.filtersCollapsed,
                }))
              }
            />
          ) : state.activeTab === "companies" ? (
            <CompanyFilters
              searchQuery={state.companyFilterKeyword}
              countryFilter={state.companyCountryFilter}
              onSearchChange={(value) => handleFilterChange("companyFilterKeyword", value)}
              onCountryChange={(value) => handleFilterChange("companyCountryFilter", value)}
              onClearFilters={handleClearFilters}
              allCompanyItems={state.allCompanyItems}
              collapsed={state.filtersCollapsed}
              onToggleCollapse={() =>
                setState((prev) => ({
                  ...prev,
                  filtersCollapsed: !prev.filtersCollapsed,
                }))
              }
            />
          ) : (
            <InventoryFiltersComponent
              searchQuery={state.inventoryFilterKeyword}
              partNumberFilter={state.inventoryPartNumber}
              categoryFilter={state.category}
              conditionFilter={state.condition}
              countryFilter={state.country}
              manufacturerFilter={state.manufacturer}
              minPriceFilter={0}
              maxPriceFilter={0}
              qtyFilter={state.minQty}
              companyFilter={state.company}
              onSearchChange={(value) => handleFilterChange("inventoryFilterKeyword", value)}
              onPartNumberChange={(value) => handleFilterChange("inventoryPartNumber", value)}
              onCategoryChange={(value) => handleFilterChange("category", value)}
              onConditionChange={(value) => handleFilterChange("condition", value)}
              onCountryChange={(value) => handleFilterChange("country", value)}
              onManufacturerChange={(value) => handleFilterChange("manufacturer", value)}
              onMinPriceChange={() => {}}
              onMaxPriceChange={() => {}}
              onQtyChange={handleQtyChange}
              onCompanyChange={handleCompanyChange}
              onClearFilters={handleClearFilters}
              allInventoryItems={state.allInventoryItems}
              collapsed={state.filtersCollapsed}
              showPriceFilter={false}
              showQtyFilter={state.activeTab === "inventory"}
              showCompanyFilter={state.activeTab === "inventory"}
              onToggleCollapse={() =>
                setState((prev) => ({
                  ...prev,
                  filtersCollapsed: !prev.filtersCollapsed,
                }))
              }
            />
          )}
        </aside>

        <div
          className={`${
            state.filtersCollapsed ? "col-11" : "col-12 col-md-8 col-lg-9"
          } bg-white border-bottom border-start-0 d-flex flex-column`}
        >
          <div className="pt-3 pb-3 px-3 d-flex justify-content-between align-items-center">
            <h2 className={`m-0 u-H2-24 ${styles.textBlack}`}>
              {state.q ? `Search Results for "${state.q}"` : "Search Results"}
            </h2>
            {(state.activeTab === "inventory" || state.activeTab === "broadcasts") && (
              <div className="d-flex align-items-center gap-2">
                <span className={`u-P3-14-medium text-nowrap ${styles.textBlack}`}>
                  Sort by:
                </span>
                <select
                  className="search-sort-dropdown"
                  value={getSortDisplayValue()}
                  onChange={(e) => handleSortChange(e.target.value)}
                  aria-label="Sort search results"
                >
                  <option value="newest">Newest</option>
                  <option value="oldest">Oldest</option>
                  {state.activeTab === "inventory" && (
                    <>
                      <option value="price_desc">Price: High → Low</option>
                      <option value="price_asc">Price: Low → High</option>
                    </>
                  )}
                </select>
              </div>
            )}
          </div>
          <ul className="nav nav-tabs search-results-tabs px-3">
            <li className="nav-item">
              <button
                className={`nav-link ${state.activeTab === "broadcasts" ? "active" : ""}`}
                onClick={() => handleTabChange("broadcasts")}
              >
                Broadcasts
              </button>
            </li>
            <li className="nav-item">
              <button
                className={`nav-link ${state.activeTab === "inventory" ? "active" : ""}`}
                onClick={() => handleTabChange("inventory")}
              >
                Inventory
              </button>
            </li>
            <li className="nav-item">
              <button
                className={`nav-link ${state.activeTab === "companies" ? "active" : ""}`}
                onClick={() => handleTabChange("companies")}
              >
                Companies
              </button>
            </li>
          </ul>

          <div className={`p-3 ${styles.contentWrapper}`}>
            {/* [2026-07-06] GUEST_ACCESS_PHASE2: guest daily soft wall (BR-13). signupRequired is
                only ever set for guests, so logged-in users always fall through to the grid. */}
            {state.activeTab === "inventory" && state.signupRequired && softWallPanel}

            {state.activeTab === "inventory" && !state.signupRequired && (
              <>
                <div ref={inventoryListRef}>
                  <InventoryGrid<Inventory>
                    items={currentTabData.items as Inventory[]}
                    loading={currentTabData.loading}
                    error={currentTabData.error}
                    hasMore={false}
                    onLoadMore={() => {}}
                    total={currentTabData.total}
                    currentPage={currentTabData.page}
                    totalPages={currentTabData.pages}
                    onRetry={handleRetry}
                    displayMode="list"
                    paginationMode={true}
                    emptyMessage={
                      state.q
                        ? "No inventory results found for your search"
                        : "Enter a search query to find inventory items"
                    }
                    renderItem={(item) => (
                      <InventoryItemCard
                        item={item}
                        displayMode="row"
                        showSellerInfo={true}
                        onClick={handleInventoryClick}
                        disableMetaHoverHide={true}
                      />
                    )}
                  />
                </div>
                {currentTabData.items.length > 0 && (
                  <PaginationControls
                    currentPage={currentTabData.page}
                    totalPages={currentTabData.pages}
                    itemsPerPage={currentTabData.itemsPerPage}
                    totalItems={currentTabData.total}
                    onPageChange={handlePageChange}
                    onItemsPerPageChange={handleItemsPerPageChange}
                    loading={currentTabData.loading}
                  />
                )}
              </>
            )}

            {state.activeTab === "broadcasts" && state.signupRequired && softWallPanel}

            {state.activeTab === "broadcasts" && !state.signupRequired && (
              <>
                <div ref={broadcastListRef}>
                  <InventoryGrid<FlattenedBroadcastItem>
                    items={currentTabData.items as FlattenedBroadcastItem[]}
                    loading={currentTabData.loading}
                    error={currentTabData.error}
                    hasMore={false}
                    onLoadMore={() => {}}
                    total={currentTabData.total}
                    currentPage={currentTabData.page}
                    totalPages={currentTabData.pages}
                    onRetry={handleRetry}
                    displayMode="list"
                    paginationMode={true}
                    emptyMessage={
                      state.q
                        ? "No broadcast results found for your search"
                        : "Enter a search query to find broadcasts"
                    }
                    renderItem={(item) => (
                      <BroadcastItemCard
                        item={item}
                        displayMode="row"
                        onClick={handleBroadcastClick}
                      />
                    )}
                    getItemKey={(item, index) =>
                      item.broadcastGroup?._id
                        ? `${item.broadcastGroup._id}-${item.itemNo || index}`
                        : `broadcast-${index}`
                    }
                  />
                </div>
                {currentTabData.items.length > 0 && (
                  <PaginationControls
                    currentPage={currentTabData.page}
                    totalPages={currentTabData.pages}
                    itemsPerPage={currentTabData.itemsPerPage}
                    totalItems={currentTabData.total}
                    onPageChange={handlePageChange}
                    onItemsPerPageChange={handleItemsPerPageChange}
                    loading={currentTabData.loading}
                  />
                )}
              </>
            )}

            {/* [2026-07-06] GUEST_ACCESS: Companies is member-only — guests get a sign-in gate. */}
            {state.activeTab === "companies" && isGuest && companiesGatePanel}

            {state.activeTab === "companies" && !isGuest && (
              <>
                <InventoryGrid<User>
                  items={currentTabData.items as User[]}
                  loading={currentTabData.loading}
                  error={currentTabData.error}
                  hasMore={false}
                  onLoadMore={() => {}}
                  total={currentTabData.total}
                  currentPage={currentTabData.page}
                  totalPages={currentTabData.pages}
                  onRetry={handleRetry}
                  displayMode="list"
                  paginationMode={true}
                  emptyMessage={
                    state.q
                      ? "No companies found for your search"
                      : "Enter a search query to find companies"
                  }
                  renderItem={(item) => (
                    <CompanyCard company={item} onClick={handleCompanyClick} />
                  )}
                />
                {currentTabData.items.length > 0 && (
                  <PaginationControls
                    currentPage={currentTabData.page}
                    totalPages={currentTabData.pages}
                    itemsPerPage={currentTabData.itemsPerPage}
                    totalItems={currentTabData.total}
                    onPageChange={handlePageChange}
                    onItemsPerPageChange={handleItemsPerPageChange}
                    loading={currentTabData.loading}
                  />
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Search;

